/**
 * NearMeToggle — founder direction 9 Feb 2026 ·
 * "(c) Both, GPS-first · top of /table next to the search bar ·
 *  not buried in the Refine panel"
 *
 * Surfaces a primary "Near me" pill ABOVE the search bar. Single
 * click → requests browser geolocation → routes the page to the
 * /nearby endpoint with the current tab. Honours the existing tab
 * filter so the member's intent (bars/clubs/michelin/all) carries
 * through.
 *
 * Doctrine:
 *  - Privacy: we never store the GPS · we pass it directly to the
 *    /nearby fetch and forget it. The browser owns permission.
 *  - Soft-fail: geolocation denial / unavailability surfaces a
 *    gentle prompt to use the search bar instead.
 */
import React, { useState, useCallback } from "react";


export function NearMeToggle({ tab, onLocated, active }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const handleClick = useCallback(() => {
        if (busy) return;
        if (!navigator.geolocation) {
            setErr("Your browser doesn't share location · use the search bar to name a city.");
            return;
        }
        setBusy(true);
        setErr(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setBusy(false);
                onLocated({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                    tab: tab || "all",
                });
            },
            (geoErr) => {
                setBusy(false);
                if (geoErr.code === 1) {
                    setErr("Location permission denied · use the search bar to name a city instead.");
                } else {
                    setErr("Couldn't get your location · try the search bar instead.");
                }
            },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
        );
    }, [busy, tab, onLocated]);

    return (
        <div data-testid="near-me-toggle-wrap" style={{
            display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
        }}>
            <button
                type="button"
                data-testid="near-me-toggle"
                data-active={active ? "true" : "false"}
                onClick={handleClick}
                disabled={busy}
                style={{
                    fontSize: 12,
                    padding: "7px 14px",
                    borderRadius: 999,
                    border: "1px solid var(--t-line2, rgba(0,0,0,0.12))",
                    background: active ? "var(--t-ink, #211C15)" : "transparent",
                    color: active ? "var(--t-paper, #F4EFE6)" : "var(--t-ink2, rgba(0,0,0,0.7))",
                    cursor: busy ? "wait" : "pointer",
                    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                    letterSpacing: "0.01em",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    transition: "background 120ms ease, color 120ms ease",
                }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
                </svg>
                {busy ? "Locating…" : "Near me"}
            </button>
            {err ? (
                <span
                    data-testid="near-me-error"
                    style={{
                        fontSize: 11,
                        color: "var(--t-ink3, rgba(0,0,0,0.55))",
                        fontStyle: "italic",
                    }}
                >
                    {err}
                </span>
            ) : null}
        </div>
    );
}
